import * as React from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useTheme } from '../../../contexts/ThemeContext'
import { cn } from '@merels/ui'
import { useSignals } from '@preact/signals-react/runtime'
import type { ShellVMDep } from '../../../deps/ui/shellVM/shellVM.types'

export interface ProfileAvatarProps {
  shellVM: ShellVMDep
  buttonRef?: React.Ref<HTMLButtonElement>
  tabIndex?: number
  isFocused?: boolean
  /** Override app accent (e.g. active turn color). */
  accentColor?: string
  accentInkColor?: string
  backgroundColor?: string
}

export function ProfileAvatar({ shellVM, buttonRef, tabIndex, isFocused, accentColor: accentColorProp, backgroundColor: backgroundColorProp }: ProfileAvatarProps) {
  useSignals()
  const { currentUser, isLoaded, signIn, signUp, signOut, openUserProfile } = shellVM.auth
  const { getAppColor } = useTheme()
  const appColorShades = getAppColor()
  const accentColor = accentColorProp ?? appColorShades.main
  const backgroundColor = backgroundColorProp ?? appColorShades.background

  if (!isLoaded.value) {
    return (
      <div
        className="h-[var(--menu-btn-size,36px)] w-[var(--menu-btn-size,36px)] animate-pulse rounded-lg bg-muted-bg"
      />
    )
  }

  const user = currentUser.value

  const handleSignIn = () => {
    signIn().catch(err => console.error('Sign in failed', err))
  }

  const handleSignUp = () => {
    signUp().catch(err => console.error('Sign up failed', err))
  }

  const handleSignOut = () => {
    signOut().catch(err => console.error('Sign out failed', err))
  }

  const tooltipText = user 
    ? `Logged in as ${user.fullName || user.primaryEmailAddress?.emailAddress || 'User'}` 
    : 'Guest mode (Click to Sign In / Sign Up)'

  return (
    <DropdownMenu.Root>
      <div className="relative inline-flex items-center justify-center group">
        <DropdownMenu.Trigger asChild>
          <button
            ref={buttonRef}
            tabIndex={tabIndex}
            className={cn(
              "flex h-[var(--menu-btn-size,36px)] w-[var(--menu-btn-size,36px)] items-center justify-center rounded-lg transition-colors overflow-hidden border border-transparent cursor-pointer",
              isFocused && "outline-none"
            )}
            style={{
              color: accentColor,
              ...(isFocused && accentColor ? {
                boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
              } : {}),
            }}
            aria-label={user ? `User menu for ${user.fullName || 'User'}` : 'Authentication menu'}
          >
            {user && user.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.fullName || 'Profile image'}
                className="h-full w-full object-cover"
              />
            ) : (
              <svg
                width="var(--menu-icon-size,20px)"
                height="var(--menu-icon-size,20px)"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
          </button>
        </DropdownMenu.Trigger>
        <span className="absolute top-[125%] left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-md bg-neutral-900/95 text-white border border-white/10 text-[10px] font-semibold shadow-lg whitespace-nowrap opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-100 pointer-events-none z-50">
          {tooltipText}
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-[1px] border-[4px] border-transparent border-b-neutral-900/95" />
        </span>
      </div>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={cn(
            "z-[11000] min-w-[200px] rounded-lg border border-basic bg-surface p-1 shadow-lg",
            "transition-all duration-100 ease-out fill-mode-forwards"
          )}
          style={{
            borderColor: accentColor || undefined,
          }}
          sideOffset={5}
          align="end"
          onCloseAutoFocus={(e) => {
            // Selecting "Me" opens the Me dialog, which manages its own initial
            // focus (the name field) - without this, Radix would return focus to
            // this trigger button on close and win the race against that.
            e.preventDefault()
          }}
        >
          {user ? (
            <>
              <div
                className="px-3 py-2 text-xs font-semibold text-secondary border-b border-basic mb-1 truncate"
                style={{ borderBottomColor: accentColor || undefined }}
              >
                {user.fullName || user.primaryEmailAddress?.emailAddress || 'User Account'}
              </div>
              <DropdownMenu.Item
                className="flex items-center w-full px-3 py-2 text-sm rounded-md cursor-pointer text-primary hover:bg-accent hover:text-inverse data-[highlighted]:bg-accent data-[highlighted]:text-inverse outline-none transition-colors text-left"
                onSelect={shellVM.actions.openMeDialog}
              >
                Me
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex items-center w-full px-3 py-2 text-sm rounded-md cursor-pointer text-primary hover:bg-accent hover:text-inverse data-[highlighted]:bg-accent data-[highlighted]:text-inverse outline-none transition-colors text-left"
                onSelect={openUserProfile}
              >
                Manage Account
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex items-center w-full px-3 py-2 text-sm rounded-md cursor-pointer text-danger hover:bg-danger-bg hover:text-inverse data-[highlighted]:bg-danger-bg data-[highlighted]:text-inverse outline-none transition-colors text-left"
                onSelect={handleSignOut}
              >
                Sign Out
              </DropdownMenu.Item>
            </>
          ) : (
            <>
              <div
                className="px-3 py-2 text-xs font-semibold text-secondary border-b border-basic mb-1"
                style={{ borderBottomColor: accentColor || undefined }}
              >
                Guest Mode
              </div>
              <DropdownMenu.Item
                className="flex items-center w-full px-3 py-2 text-sm rounded-md cursor-pointer text-primary hover:bg-accent hover:text-inverse data-[highlighted]:bg-accent data-[highlighted]:text-inverse outline-none transition-colors text-left"
                onSelect={shellVM.actions.openMeDialog}
              >
                Me
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex items-center w-full px-3 py-2 text-sm rounded-md cursor-pointer text-primary hover:bg-accent hover:text-inverse data-[highlighted]:bg-accent data-[highlighted]:text-inverse outline-none transition-colors text-left"
                onSelect={handleSignIn}
              >
                Sign In
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex items-center w-full px-3 py-2 text-sm rounded-md cursor-pointer text-primary hover:bg-accent hover:text-inverse data-[highlighted]:bg-accent data-[highlighted]:text-inverse outline-none transition-colors text-left"
                onSelect={handleSignUp}
              >
                Create Account
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
